import React, { useEffect, useState } from 'react'
import QRCode from 'react-qr-code'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import useFirebaseQRAuthService from '@src/firebase-util/firebase-qr-auth-service'

import { setRoom } from '@store/actionSlices/appConfig'
import {
  removeProjectIdentity,
  setProjectIdentity,
} from '@store/actionSlices/projectIdentity'
import {
  TokenPayloadInterface,
  removeToken,
  setToken,
} from '@store/actionSlices/token'
import { DEFAULT_SHOWCASE_ROOM } from '@store/constants'

import { Auth } from '@api/authentication'

import { createSessionId, generateId } from '@adUtilities/generate-id-util'
import { notifyError } from '@adUtilities/notifier'

import Skeleton from './skeleton'

const LOCALSTORAGE_KEY_QR_AUTH_REF = 'qrAuthRef'

function QRAuth() {
  const history = useHistory()
  const dispatch = useDispatch()

  const [isLoading, setLoadingState] = useState(true)
  const [isDeleting, setDeleteState] = useState(false)
  const [qrAuthCode, setQRAuthCode] = useState('')

  const {
    qrAuthData,
    createDocumentIfNotExists,
    updateDocument,
    deleteDocument,
    createSession,
    doesSessionExist,
  } = useFirebaseQRAuthService(qrAuthCode)

  const handleBackToLoginPage = async () => {
    setDeleteState(true)
    const qrAuthKey = localStorage.getItem(LOCALSTORAGE_KEY_QR_AUTH_REF)

    if (qrAuthKey) {
      await deleteDocument(qrAuthKey)
      localStorage.removeItem(LOCALSTORAGE_KEY_QR_AUTH_REF)
    }
    setQRAuthCode('')
    setDeleteState(false)
    history.push('/')
  }

  const generateQRAuthCodeIfNotExists = React.useCallback(async () => {
    setLoadingState(true)
    let qrAuthKey = localStorage.getItem(LOCALSTORAGE_KEY_QR_AUTH_REF)

    if (!qrAuthKey) {
      const key = generateId(6)
      localStorage.setItem(LOCALSTORAGE_KEY_QR_AUTH_REF, key)
      qrAuthKey = key
    }

    await createDocumentIfNotExists(qrAuthKey, {
      authData: '',
      sessionId: '',
      createdAt: Date.now(),
    })
    setQRAuthCode(qrAuthKey)
    setLoadingState(false)
  }, [])

  const triggerError = (): void => {
    dispatch(removeToken())
    dispatch(removeProjectIdentity())
    console.error('Something went wrong, please rescan the QR')
    notifyError('Something went wrong, please rescan the QR')
  }

  const handleQRAuthLogin = React.useCallback(
    async (authData: string) => {
      const authDataObject: Omit<Auth, 'showcaseRoom'> & {
        showcaseRoom?: string
      } = JSON.parse(authData)

      const projectId = authDataObject?.project?.id
      const projectName = authDataObject?.project?.name
      const projectCanonicalName = authDataObject?.project?.canonical_name
      const user = authDataObject?.user
      const showcaseRoom = authDataObject?.showcaseRoom

      if (!projectId || !projectName || !projectCanonicalName || !user) {
        triggerError()
        return
      }

      dispatch(setRoom(showcaseRoom || DEFAULT_SHOWCASE_ROOM))

      const sessionId = createSessionId()

      dispatch(
        setProjectIdentity({
          projectName: projectCanonicalName,
          projectId,
          projectLabel: projectName,
          sessionId: [
            {
              key: sessionId,
              project: projectCanonicalName,
              room: '',
            },
          ],
          masterKey: '',
        })
      )

      dispatch(setToken(user as TokenPayloadInterface))

      const sessionCreateResponse: boolean = await createSession({
        projectName: projectCanonicalName,
        sessionId,
      })

      if (!sessionCreateResponse) {
        triggerError()
        return
      }

      const sessionExist: boolean = await doesSessionExist({
        projectName: projectCanonicalName,
        sessionId,
      })

      if (!sessionExist) {
        triggerError()
        return
      }

      await updateDocument(qrAuthCode, {
        authData: '',
        sessionId,
      })

      localStorage.removeItem(LOCALSTORAGE_KEY_QR_AUTH_REF)
    },
    [qrAuthCode]
  )

  useEffect(() => {
    generateQRAuthCodeIfNotExists()
  }, [])

  useEffect(() => {
    if (!qrAuthData) {
      return
    }

    const { authData = '' } = qrAuthData

    if (!authData) {
      return
    }

    handleQRAuthLogin(authData)
  }, [qrAuthData?.authData])

  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="flex w-[300px] flex-col items-center space-y-16 rounded bg-white px-8 pb-8 pt-6">
        <div className="w-full text-center">Showcase</div>
        <div className="w-full">
          {isLoading ? (
            <div className="h-[236px] w-full rounded">
              <Skeleton />
            </div>
          ) : (
            <QRCode
              value={JSON.stringify({
                issuer: 'showcase',
                code: qrAuthCode,
              })}
              style={{
                height: 'auto',
                maxWidth: '100%',
                width: '100%',
              }}
            />
          )}
        </div>
        <div className="w-full">
          <button
            className={`focus:shadow-outline w-full rounded px-4 py-2 text-white focus:outline-none ${
              !isDeleting
                ? 'bg-sky-500 hover:bg-sky-700'
                : 'bg-gray-500 hover:bg-gray-700'
            }`}
            type="button"
            disabled={isDeleting}
            onClick={handleBackToLoginPage}
          >
            Back to Sign in page
          </button>
        </div>
      </div>
    </div>
  )
}

export default QRAuth
